'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage.hook';
import { RDD2025 } from './custom-ui/RDD-2025';
import { EventPath } from '@lib/flags';

export const WEBFLOW_PATH = 'https://event.3billion.io';

type EventLayerProps = {
  eventPath: EventPath | null;
};

const IFRAME_LOAD_TIMEOUT_MS = 3000;

export function EventLayer({ eventPath }: EventLayerProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const { getStorage, removeStorage } = useLocalStorage();
  const [isLayerOpen, setIsLayerOpen] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isClosedPermanently = getStorage(eventPath?.mainPath ?? '') === 'true';
  const isInDuration =
    new Date(eventPath?.startDate || '') <= new Date() &&
    new Date(eventPath?.endDate || '') >= new Date();

  const handleIframeLoaded = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  useEffect(() => {
    setIframeLoaded(false);
  }, [eventPath]);

  // iframe onLoad 이벤트가 불안정할 수 있으므로 타임아웃 fallback 추가
  useEffect(() => {
    if (iframeLoaded) return;

    const timeoutId = setTimeout(() => {
      setIframeLoaded(true);
    }, IFRAME_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [iframeLoaded, eventPath]);

  // 이벤트 기간 종료 시 로컬스토리지에서 키 삭제
  useEffect(() => {
    if (!isInDuration && isClosedPermanently) {
      removeStorage(eventPath?.mainPath || '');
    }
  }, [isInDuration, isClosedPermanently, eventPath, removeStorage]);

  // 모바일에서 레이어 터치시 뒷레이어 터치 방지
  const isVisible =
    isLayerOpen && !!eventPath && isInDuration && !isClosedPermanently;

  useEffect(() => {
    function preventTouchMove(e: TouchEvent) {
      e.preventDefault();
    }

    if (isVisible) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventTouchMove, {
        passive: false,
      });
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventTouchMove);
    };
  }, [isVisible]);

  if (!isInDuration) return null;
  if (!eventPath) return null;
  if (isClosedPermanently) return null;
  if (!isLayerOpen) return null;

  return (
    <div className="fixed left-0 top-0 z-[10000] h-[100dvh] w-[100vw] bg-[#040435]">
      <div className="fixed left-0 top-0 z-[10000] h-[100dvh] w-[100vw] bg-[#040435]">
        <iframe
          ref={iframeRef}
          width="100%"
          height="100%"
          src={`${WEBFLOW_PATH}/${eventPath?.mainPath}`}
          title="3billion Event"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="no-referrer"
          allowFullScreen
          onLoad={handleIframeLoaded}
        />
        {iframeLoaded && (
          <RDD2025
            eventPath={eventPath}
            onClose={() => setIsLayerOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
